/**
 * Data models for the Time Tracking History feature.
 *
 * Represents the Firestore data at:
 *   `clients/{clientId}/users/{userId}/timeTracking/{monthDocId}`
 *
 * Month document IDs use the format `YYYY-M{zeroIndexedMonth}`, e.g. `2026-M4` = May 2026.
 * Each month document contains a `days` map keyed by `"MM-DD"`.
 *
 * The Tagesverlauf (day timeline) builds a chronological event list from
 * projectEntries, otherEntries, computed gaps, and Start/End of Day markers.
 * additionalEntries and autoTimeTrackingEntries are NOT shown in the timeline.
 */

import { ProjectOperationSource } from "@/data/models/ProjectDetails";
import { GeoLocation } from "@/utils/GeocodingService";

/** Source of a suspend entry event. */
export enum SuspendEntrySource {
  AUTO = "AUTO",
  MANUAL = "MANUAL",
}

/** Suspend entry event type used for pause/resume and silent interruption logic. */
export enum SuspendType {
  S_SUSPEND = "S_SUSPEND",
  P_SUSPEND = "P_SUSPEND",
  S_RESUME = "S_RESUME",
}

/** Task values stored on project time-tracking entries. */
export enum TimeTrackingProjectTask {
  ORDER_ENTRY = "ORDER_ENTRY",
  FIRST_CONTACT = "FIRST_CONTACT",
  LEAK_DETECTION = "LEAK_DETECTION",
  DAMAGE_ASSESSMENT = "DAMAGE_ASSESSMENT",
  INSURANCE_OFFER = "INSURANCE_OFFER",
  DRYING_APPROVAL = "DRYING_APPROVAL",
  DRYING_PREPARATION = "DRYING_PREPARATION",
  INTERMEDIATE_MEASUREMENT = "INTERMEDIATE_MEASUREMENT",
  DRYING_CONCLUSION = "DRYING_CONCLUSION",
  REPAIR = "REPAIR",
  PROJECT_CONCLUSION = "PROJECT_CONCLUSION",
}

/**
 * Persisted reason a task time-tracking entry was closed.
 *
 * Written by the closing app (IRES Work / IRES Project) and by the Cloud stale
 * auto-close when it sets the entry's end. Both a deliberate suspend and a normal
 * finish set `workEndTime`/`endedAtDate`, so the timestamps alone cannot tell them
 * apart — this field carries that intent.
 *
 * Absent on legacy entries: treated as {@link TimeTrackingEndReason.COMPLETED} for
 * display, preserving the historical "closed = finished" appearance.
 */
/**
 * What IRES Work's travel planner does with a user's position (FAHRT).
 *
 * Hans, 18.08: "Add a user setting - navigation/simulation/off". A per-user preference rather than a
 * client policy, so one tester can simulate a drive while a colleague really drives. Office writes it on
 * the user record and does nothing else with it; IRES Work is the only reader.
 */
export enum TravelMode {
  /** The device's own GPS. What every drive used before the setting existed, and the default. */
  NAVIGATION = "NAVIGATION",
  /** A drive invented along the calculated route. Entries it produces carry `isSimulated`. */
  SIMULATION = "SIMULATION",
  /** No position at all: the planner keeps its screens without map, route or figures. */
  OFF = "OFF",
}

/**
 * How close to its destination a drive counts as arrived, in metres (FAHRT).
 *
 * Hans's figure, 17.08: beyond this the travel planner offers UNTERBRECHEN rather than ANGEKOMMEN, so a
 * crew that stopped short cannot record an arrival by reflex. Straight-line distance. Stored on the
 * client's `userSettings` document as {@link UserSettings.travelArrivalRadiusInMeters} and read by IRES
 * Work; Office edits it and does nothing else with it.
 */
export const DEFAULT_TRAVEL_ARRIVAL_RADIUS_IN_METERS = 200;

/** Below this, GPS noise alone would decide which button the crew sees. */
export const MIN_TRAVEL_ARRIVAL_RADIUS_IN_METERS = 20;

/** Beyond this, "arrived" would cover most of a village. */
export const MAX_TRAVEL_ARRIVAL_RADIUS_IN_METERS = 5000;

export enum TimeTrackingEndReason {
  /** The task was deliberately suspended (phase suspended, work paused for later). */
  SUSPENDED = "SUSPENDED",
  /** The task was finished/concluded normally. */
  COMPLETED = "COMPLETED",
}

/**
 * Lifecycle state of a task time-tracking entry, DERIVED for display only (never
 * persisted). See {@link deriveEntryState} in the repository for the rule:
 * open ⇒ RUNNING; closed + endReason SUSPENDED ⇒ SUSPENDED; closed otherwise ⇒ FINISHED.
 */
export enum TimeTrackingEntryState {
  RUNNING = "RUNNING",
  SUSPENDED = "SUSPENDED",
  FINISHED = "FINISHED",
}

/** Context values stored on project-level auto time-tracking logs. */
export enum AutoTimeTrackingContext {
  LEAK_DETECTION = "LEAK_DETECTION",
  DAMAGE_ASSESSMENT = "DAMAGE_ASSESSMENT",
  PREPARE_DRYING = "PREPARE_DRYING",
  FINAL_MEASUREMENT = "FINAL_MEASUREMENT",
  FINAL_DRYING = "FINAL_DRYING",
  REPAIR = "REPAIR",
}

/** Metadata about who recorded/changed an action and when. */
export interface RecordedBy {
  userId?: string;
  recordedAt: firebase.firestore.Timestamp;
  recordedByUserId: string;
}

/** A suspend/resume event within a project or other entry. */
export interface SuspendEntry {
  source: SuspendEntrySource;
  startTime: firebase.firestore.Timestamp;
  suspendType: SuspendType;
  /** Which app produced this event (e.g. PROJECT_MANAGER_APP, DEVICE_MANAGER_APP). */
  operationSource?: ProjectOperationSource | null;
}

/** Itemised work breakdown (older entry format). */
export interface TimeTrackingWorkItem {
  timeInMin: number;
  name: string;
  /** FQN type discriminator for polymorphic sub-entries. */
  type?: string;
  categoryType?: string;
  startTime?: firebase.firestore.Timestamp | null;
  endTime?: firebase.firestore.Timestamp | null;
  source?: SuspendEntrySource;
  /** For ProjectWorkTrackingEntry: step info for task label resolution. */
  stepAtStart?: { task?: string } | null;
  stepAtEnd?: { task?: string } | null;
  /** For ProjectTravelTrackingEntry. */
  startLocation?: string | null;
  endLocation?: string | null;
  /** Whether this sub-entry's start was manually edited. */
  wasStartEdited?: boolean;
  /** Whether this sub-entry's end was manually edited. */
  wasEndEdited?: boolean;
}

/**
 * A time entry linked to a project.
 *
 * Newer entries (2026+) have `workStartTime`/`workEndTime` and `task`.
 * Older entries use `data[]` for itemised work and `createdBy.recordedAt` for timing.
 */
export interface TimeTrackingProjectEntry {
  id: string;
  projectName: string;
  projectId: string;
  projectPhase: string;

  /** Specific task within the phase (newer format). */
  task?: TimeTrackingProjectTask | string;
  /** When work on this entry started (newer format). */
  workStartTime?: firebase.firestore.Timestamp;
  /** When work on this entry ended (newer format). */
  workEndTime?: firebase.firestore.Timestamp | null;

  travelingTimeInMin: number;
  countsAsTravel?: boolean;
  /** When true, this project entry represents a project-task pause segment. */
  countsAsPause?: boolean | null;

  /** Itemised work breakdown (older format). */
  data: TimeTrackingWorkItem[];

  wasCreatedOnOtherDay: boolean;
  contactPersonName?: string | null;
  suspendEntries?: SuspendEntry[];
  userSetStartOfWork?: firebase.firestore.Timestamp | null;
  userSetEndOfWork?: firebase.firestore.Timestamp | null;
  lastChangedBy?: RecordedBy | null;
  createdBy: RecordedBy;

  /** Legacy start date used in older entries. */
  startDate?: firebase.firestore.Timestamp | null;
  /** Which app opened this entry (e.g. PROJECT_MANAGER_APP, DEVICE_MANAGER_APP). */
  startSource?: ProjectOperationSource | null;
  /** Which app closed this entry. */
  endSource?: ProjectOperationSource | null;
  /**
   * Why the entry was closed (suspend vs finish). Absent on legacy/open entries.
   * Inherited by {@link SharedTimeTrackingEntry}. See {@link TimeTrackingEndReason}.
   */
  endReason?: TimeTrackingEndReason | string | null;
  /**
   * Client-side marker: set when this entry was resolved from a shared (team)
   * reference for display. Not persisted; lets the UI flag team entries.
   */
  isSharedEntry?: boolean;

  /**
   * The travel fields IRES Work writes on an ANFAHRT, which is a project entry with
   * `countsAsTravel`. Same meanings as on {@link TimeTrackingOtherEntry}.
   */
  startCoordinates?: GeoLocation | null;
  endCoordinates?: GeoLocation | null;
  isSimulated?: boolean | null;
  routeOrigin?: GeoLocation | null;
  routeDestination?: GeoLocation | null;
  routeDestinationAddress?: string | null;
  estimatedArrivalTime?: firebase.firestore.Timestamp | null;
}

/**
 * A reference, held on a user's day, to a shared time-tracking entry.
 *
 * Reference-only by design: it carries just the id, and the authoritative open/close +
 * timing state is resolved live from the shared doc (never copied per-user, to avoid
 * N-way sync). Optional display-only fields may be denormalized for cheap listing, but
 * they must never be treated as the source of truth.
 */
export interface SharedTimeTrackingEntryReference {
  /**
   * Document id in `clients/{clientId}/sharedTimeTrackingEntries`. Equals the
   * project-level `autoTimeTrackingLog` id (extends the individual-case invariant
   * user `projectEntry.id` === project log id).
   */
  sharedEntryId: string;
}

/**
 * A time entry shared by a TEAM working a single task, stored at
 * `clients/{clientId}/sharedTimeTrackingEntries/{id}`.
 *
 * Same shape as a user-level {@link TimeTrackingProjectEntry} plus the roster. It is the
 * single source of truth for the shared segment: every involved user (INCLUDING the main
 * responsible user) holds only a {@link SharedTimeTrackingEntryReference} on their day —
 * no private `projectEntry`. Closing it once closes it for everyone.
 *
 * `id` equals the project-level `autoTimeTrackingLog` id.
 *
 * NOTE on `additionalUsers`: here it lists the userIds of ALL involved users (main +
 * additional). This differs from the *appointment* `additionalUsers` (extra users beyond
 * the responsibleUser) — see the shared-time-tracking reference doc.
 */
export interface SharedTimeTrackingEntry extends TimeTrackingProjectEntry {
  /** userIds of all users sharing this entry (main + additional). */
  additionalUsers: string[];
}

/**
 * A non-project time entry (pause, travel, internal work, interruption).
 */
export interface TimeTrackingOtherEntry {
  id: string;
  categoryName: string;
  categoryType?: string;
  countsAsPause: boolean;
  countsAsTravel?: boolean;
  countsAsInterruption?: boolean;
  timeInMin: number;
  travelingTimeInMin: number;
  location?: string | null;

  /** Actual work start (newer format). */
  workStartDate?: firebase.firestore.Timestamp;
  /** Actual work end (newer format). */
  workEndDate?: firebase.firestore.Timestamp;
  /** User-overridden start time. */
  userSetStartOfWork?: firebase.firestore.Timestamp | null;
  /** User-overridden end time. */
  userSetEndOfWork?: firebase.firestore.Timestamp | null;

  /** Legacy start date. */
  startDate?: firebase.firestore.Timestamp | null;
  wasCreatedOnOtherDay: boolean;
  lastChangedBy?: RecordedBy | null;
  createdBy?: RecordedBy;
  suspendEntries?: SuspendEntry[];
  /** Which app opened this entry (e.g. PROJECT_MANAGER_APP, DEVICE_MANAGER_APP). */
  startSource?: ProjectOperationSource | null;
  /** Which app closed this entry. */
  endSource?: ProjectOperationSource | null;

  /**
   * Where the crew actually was when this travel entry opened and closed, from the device's GPS.
   *
   * Written by IRES Work, on travel entries only. Recorded for checking a drive afterwards and
   * displayed nowhere in Office today: whether they are ever shown is a product decision, and showing a
   * position a crew did not know was being kept is not one to take by accident.
   */
  startCoordinates?: GeoLocation | null;
  endCoordinates?: GeoLocation | null;
  /** True when those positions came from IRES Work's simulated drive rather than from a device. */
  isSimulated?: boolean | null;
  /**
   * What the drive was planned as: the point it was planned from, the point the crew confirmed as the
   * destination, and what that point is called.
   *
   * IRES Work stores no route, no duration and no distance: those are derived, and every screen
   * recalculates them from these two points and the reader's own position. Office can therefore draw a
   * drive whenever it wants to, at the cost of one Routes lookup.
   */
  routeOrigin?: GeoLocation | null;
  routeDestination?: GeoLocation | null;
  routeDestinationAddress?: string | null;
  /**
   * The arrival time the drive set off with: its start plus the planned route's duration.
   *
   * Written once by IRES Work when the drive starts (from 2026-09-24, Hans 19.09), and never changed or
   * cleared afterwards, so a drive running without location keeps the ETA it started with through an app
   * restart. The one deliberate exception to "nothing derived is stored": it is the planned arrival, not
   * a current one. Absent when the drive started without a route (nothing chosen, or offline). The
   * planned duration is `estimatedArrivalTime` minus the entry's start (`workStartDate` here,
   * `workStartTime` on an ANFAHRT).
   */
  estimatedArrivalTime?: firebase.firestore.Timestamp | null;
  /**
   * Why a drive's entry was closed: `SUSPENDED` for UNTERBRECHEN, `COMPLETED` for ANGEKOMMEN.
   *
   * Written by IRES Work from 2026-08-19 on travel entries only (Hans, 17.08: "In any case we suspend TT
   * when the drive is finished — it is never suspended"). Before that a drive carried no reason at all, so
   * one abandoned half way was indistinguishable from one that arrived. Absent on every other kind of
   * `other` entry, and on legacy drives, which {@link deriveEntryState} reads as finished.
   */
  endReason?: TimeTrackingEndReason | string | null;
}

/** Legacy simple time record. */
export interface TimeTrackingAdditionalEntry {
  name: string;
  categoryType: string;
  timeInMin: number;
  totalTimeInMin: number;
  startDate: firebase.firestore.Timestamp;
}

/**
 * An auto-tracked project time entry (device-triggered).
 * Stored in `autoTimeTrackingEntries` map within a day.
 */
export interface AutomaticProjectTimeTracking {
  id: string;
  projectName: string;
  projectId: string;
  projectPhase: string;
  /** Duration in minutes (stored, pre-computed). */
  duration: number;
  /** Context that may override projectPhase for display. */
  autoTimeTrackingContext?: AutoTimeTrackingContext | string | null;
  startedAtDate?: firebase.firestore.Timestamp | null;
  endedAtDate?: firebase.firestore.Timestamp | null;
  startedByUserId?: string | null;
  endedBy?: string | null;
  createdBy: RecordedBy;
  wasCreatedOnOtherDay: boolean;
}

/**
 * A project-level auto time tracking log entry.
 * Stored at `clients/{clientId}/projects/{projectId}/logs/data` → `autoTimeTrackingLogs` map.
 *
 * Written by IRES Work when project time tracking starts/ends.
 * Contains entries from all users who worked on the project.
 */
export interface ProjectTimeTrackingLog {
  id: string;
  startedAtDate: firebase.firestore.Timestamp | null;
  /** Actual/auto-proposed end. On stale auto-close this holds the auto-proposed end. */
  endedAtDate: firebase.firestore.Timestamp | null;
  /** User-overridden start time (parity with user-level entries). */
  userSetStartOfWork?: firebase.firestore.Timestamp | null;
  /** User-overridden end time; set only when the user edited the proposed end. */
  userSetEndOfWork?: firebase.firestore.Timestamp | null;
  /** Set when app is backgrounded, cleared on resume. Null = not suspended. */
  suspendedAt: firebase.firestore.Timestamp | null;
  startedByUserId: string;
  /**
   * Present on shared (team) tasks: userIds of ALL involved users (main + additional).
   * The log id equals the shared entry id. Absent/empty for individual entries.
   */
  additionalUsers?: string[];
  endedBy: string | null;
  projectPhase: string;
  autoTimeTrackingContext: AutoTimeTrackingContext | string | null;
  /** When true, this log entry represents a travel (Anfahrt) segment. */
  countsAsTravel?: boolean | null;
  /** When true, this log entry represents a pause segment. */
  countsAsPause?: boolean | null;
  createdBy: {
    recordedAt: firebase.firestore.Timestamp;
    recordedByUserId: string;
  };
  event: string;
  useForReport: boolean;
  openedDeviceId: string | null;
  closedByDeviceId: string | null;
  /** Present on some entries. */
  entryComplete?: boolean;
  /** Present on some entries. */
  stability?: number;
  /** Which app opened this log entry (e.g. PROJECT_MANAGER_APP, DEVICE_MANAGER_APP). */
  startSource?: ProjectOperationSource | null;
  /** Which app closed this log entry. */
  endSource?: ProjectOperationSource | null;
  /**
   * Why the log entry was closed (suspend vs finish). Absent on legacy/open entries.
   * See {@link TimeTrackingEndReason}.
   */
  endReason?: TimeTrackingEndReason | string | null;
}

/**
 * A single day's time tracking data.
 */
export interface TimeTrackingDayEntry {
  id: string;
  date: firebase.firestore.Timestamp;
  startOfWork: firebase.firestore.Timestamp | null;
  endOfWork: firebase.firestore.Timestamp | null;
  userSetStartOfWork?: firebase.firestore.Timestamp | null;
  userSetEndOfWork?: firebase.firestore.Timestamp | null;
  closedBy: RecordedBy | null;
  initiallyClosedBy?: RecordedBy | null;
  reopenedBy?: RecordedBy | null;
  lastChangedBy?: RecordedBy | null;
  projectEntries: Record<string, TimeTrackingProjectEntry>;
  otherEntries: Record<string, TimeTrackingOtherEntry>;
  additionalEntries: TimeTrackingAdditionalEntry[];
  autoTimeTrackingEntries?: Record<string, AutomaticProjectTimeTracking>;
  /**
   * References to shared (team) entries this user is part of on this day. Keyed by the
   * shared entry id; the authoritative entry lives in `sharedTimeTrackingEntries`.
   */
  sharedEntries?: Record<string, SharedTimeTrackingEntryReference>;
  startOfWorkLocation?: unknown | null;
  endOfWorkLocation?: unknown | null;
}

/**
 * A month-level time tracking document.
 *
 * Firestore path: `clients/{clientId}/users/{userId}/timeTracking/{id}`
 * Document ID format: `YYYY-M{zeroIndexedMonth}` (e.g. `2026-M4` = May 2026).
 */
export interface TimeTrackingMonth {
  id: string;
  userId: string;
  startDate: firebase.firestore.Timestamp;
  endDate: firebase.firestore.Timestamp;
  days: Record<string, TimeTrackingDayEntry>;
}

// ── Timeline entry types for Tagesverlauf display ──

/** Timeline entry type for the day history view. */
export type TimelineEntryType =
  | "startOfDay"
  | "endOfDay"
  | "general"
  | "project"
  | "pause"
  | "travel"
  | "interruption"
  | "silentInterruption"
  | "other";

/** Color hex constants for timeline entries. */
export const TIMELINE_COLORS = {
  green100: "#4CAF50",
  brown105: "#A17247",
  baseYellow: "#F2B400",
  blue100: "#4681B4",
  grey100: "#788282",
  green50: "#A5D7A7",
  brown50: "#D4BBA5",
  red100: "#E6323C",
} as const;

/** A sub-entry within a project block (work task, travel, or pause). */
export interface TimelineSubEntry {
  id: string;
  type: "work" | "travel" | "pause";
  countsAsTravel?: boolean;
  /** The task enum value (e.g. "DRYING_PREPARATION") for merging consecutive same-task entries. */
  task?: string | null;
  startTime: Date | null;
  endTime: Date | null;
  label: string;
  wasStartEdited: boolean;
  wasEndEdited: boolean;
  /** True when a displayed timestamp/duration was clipped to selected day bounds. */
  wasDayClamped?: boolean;
  /** True when this displayed sub-entry represents multiple merged raw project entries. */
  wasMergedBoundaryAdjusted?: boolean;
  /** Which app opened this sub-entry (PROJECT_MANAGER_APP → 'P', DEVICE_MANAGER_APP → 'W'). */
  startSource?: ProjectOperationSource | null;
  /** Which app closed this sub-entry. */
  endSource?: ProjectOperationSource | null;
  /**
   * True when this sub-entry was closed by an unpaired silent suspend (S_SUSPEND):
   * its `endTime`/`endSource` come from the suspend event and the description shows
   * "Stille Unterbrechung".
   */
  silentlyInterrupted?: boolean;
}

/**
 * A unified entry for display in the Tagesverlauf timeline.
 * Built by `buildTimelineEntries()` in the repository.
 */
export interface TimeTrackingDisplayEntry {
  id: string;
  type: TimelineEntryType;
  /** Start time used for sorting. Null for endOfDay if day not ended. */
  startTime: Date | null;
  /** End time, or null if still running / not applicable. */
  endTime: Date | null;
  /** Main label to display. */
  label: string;
  /** Hex color for left accent bar. */
  color: string;
  /** Whether end time is null on an active entry (timer running). */
  isRunning: boolean;
  /**
   * Derived lifecycle state (project entries only): RUNNING / SUSPENDED / FINISHED.
   * Drives the phase-chip color. Undefined for non-project timeline entries.
   */
  state?: TimeTrackingEntryState;
  /** Whether the start time was manually edited by user. */
  wasStartEdited: boolean;
  /** Whether the end time was manually edited by user. */
  wasEndEdited: boolean;
  /** True when a displayed timestamp/duration was clipped to selected day bounds. */
  wasDayClamped?: boolean;
  /** True when boundaries come from merging adjacent entries into one display block. */
  wasMergedBoundaryAdjusted?: boolean;
  /** Project name (project entries only). */
  projectName?: string;
  /** Project ID (project entries only). */
  projectId?: string;
  /** Phase label translation key (project entries only). */
  phaseLabel?: string;
  /** Sub-entries within a project block. */
  subEntries?: TimelineSubEntry[];
  /** Entry-level task (for sub-entry re-grouping during merge). */
  task?: string | null;
  /** True when this project entry should be treated as travel when regrouping merged blocks. */
  countsAsTravel?: boolean;
  /** True when this project entry should be treated as pause when regrouping merged blocks. */
  countsAsPause?: boolean;
  /** Raw suspend entries from Firestore, carried forward for P_SUSPEND pairing during merge. */
  suspendEntries?: SuspendEntry[];
  /** Which app opened this entry (PROJECT_MANAGER_APP → 'P', DEVICE_MANAGER_APP → 'W'). */
  startSource?: ProjectOperationSource | null;
  /** Which app closed this entry. */
  endSource?: ProjectOperationSource | null;
  /**
   * True when this open entry was closed by an unpaired silent suspend (S_SUSPEND):
   * its `endTime`/`endSource` come from the suspend event and the description shows
   * "Stille Unterbrechung".
   */
  silentlyInterrupted?: boolean;
}

/**
 * Summary statistics for a day.
 */
export interface TimeTrackingDaySummary {
  /** Total time window in minutes (endOfWork - startOfWork). */
  totalMinutes: number;
  /** Total travel time in minutes. */
  travelMinutes: number;
  /** Total pause time in minutes. */
  pauseMinutes: number;
  /** Total other (internal) work in minutes. */
  otherMinutes: number;
  /** Total project time in minutes. */
  projectMinutes: number;
  /** Calculated working time = totalMinutes - pauseMinutes - interruptionMinutes. */
  workingMinutes: number;
  /** Unexplained time = totalMinutes - (projectMinutes + travelMinutes + otherMinutes + pauseMinutes + interruptionMinutes). */
  unexplainedMinutes: number;
  /** Whether the day is closed. */
  isClosed: boolean;
}

// ── Stale open entry auto-close callable contracts ──

/**
 * Source used to derive the proposed end time for a stale open entry.
 * LAST_ACTIVITY = the entry's latest recorded activity event (any suspend/resume) after its start;
 * ENTRY_START_TIME = fallback when there is no post-start activity event.
 */
export enum ProposalSource {
  LAST_ACTIVITY = "LAST_ACTIVITY",
  ENTRY_START_TIME = "ENTRY_START_TIME",
}

/** Outcome codes returned by the applyOpenTimeTrackingAutoClose callable. */
export enum StaleCloseOutcome {
  APPLIED = "APPLIED",
  ALREADY_CLOSED = "ALREADY_CLOSED",
  CONFLICT_RETRY = "CONFLICT_RETRY",
  INVALID_STATE = "INVALID_STATE",
  NOT_FOUND = "NOT_FOUND",
}

/** Request payload for the previewOpenTimeTrackingAutoClose Cloud callable. */
export interface PreviewOpenTimeTrackingAutoCloseRequest {
  /** Client's current time in ms, passed for Cloud diagnostic/logging purposes. */
  clientNowMs?: number;
  /**
   * When false, only project/shared entries are considered (non-project "other" entries
   * ignored). Set false for the implicit, dialog-less close path (all users); true only in
   * the dialog path (TT enabled + IRES user). Defaults to true on the Cloud side.
   */
  includeNonProjectEntries?: boolean;
}

/** Proposal details within a preview response. */
export interface StaleCloseProposal {
  /** Auto-proposed end time in ms (last S_SUSPEND or entry start as fallback). */
  proposedEndAtMs?: number;
  /** Which source was used to compute the proposal. */
  proposalSource?: ProposalSource;
  /** The S_SUSPEND timestamp used as the proposal candidate, if applicable. */
  candidateSuspendAtMs?: number | null;
  /** The entry start time in ms, used as fallback candidate. */
  entryStartAtMs?: number | null;
}

/** Feature-gate policy returned with the preview, determining the UX path in Office. */
export interface StaleClosePolicy {
  /** Whether the time tracking feature is enabled for the client. */
  timeTrackingEnabled: boolean;
  /** When true, Office must show a confirmation dialog before applying. */
  requiresUserConfirmation: boolean;
}

/** Response from the previewOpenTimeTrackingAutoClose Cloud callable. */
export interface PreviewOpenTimeTrackingAutoCloseResponse {
  /** True when a stale (unclosed, previous-day) entry was found. */
  hasStaleOpenEntry: boolean;
  /** Day map key (MM-DD) of the stale entry's day. */
  staleDayKey?: string;
  /** Month document ID (YYYY-M{month0}) of the stale entry. */
  staleMonthDocId?: string;
  /** Whether the stale entry is a project entry, a non-project entry, or a shared (team) entry. */
  entryType?: "project" | "other" | "shared";
  /** Firestore entry ID within the day's projectEntries or otherEntries map (or shared-entry id). */
  entryId?: string;
  /** Project ID if the stale entry is a project (or shared) entry. */
  projectId?: string | null;
  /** Matching project-level auto time-tracking log ID, if determinable. */
  projectLogId?: string | null;
  /** Project name for display in the Office confirmation dialog. */
  projectName?: string | null;
  /** Office project number (clientData.projectNumber), if any. */
  projectNumberOffice?: string | null;
  /** IRES project number (clientData.projectNumberIres), if any. */
  projectNumberIres?: string | null;
  /** The open task (TimeTrackingProjectTask value) for a project/shared entry, if any. */
  task?: string | null;
  /** Proposed close details. */
  proposal: StaleCloseProposal;
  /** Feature-gate policy that determines how Office should handle this. */
  policy: StaleClosePolicy;
  /** Short-lived token binding the preview to a specific apply call (recommended). */
  lockToken?: string;
  /**
   * Present when there is NO open entry to close, but the most-recent prior-day entry was
   * already auto-closed overnight by the scheduled sweep and still awaits the user's review
   * (its `pendingUserReview` flag is set). The client shows the adjust dialog (time-tracking
   * enabled only) and resolves it via apply with `resolveReview: true`. `hasStaleOpenEntry`
   * is `false` in this case — the two are mutually exclusive.
   */
  pendingReview?: PendingTimeTrackingReview | null;
}

/**
 * A previously auto-closed entry awaiting the user's review/adjustment on next login.
 * `proposedEndAtMs` is the persisted auto-close end (what the dialog shows and lets the user
 * edit). Resolving only adjusts `userSetEndOfWork`; it never re-closes the entry.
 */
export interface PendingTimeTrackingReview {
  staleDayKey: string;
  staleMonthDocId: string;
  entryType: "project" | "other" | "shared";
  entryId: string;
  projectId?: string | null;
  projectLogId?: string | null;
  projectName?: string | null;
  projectNumberOffice?: string | null;
  projectNumberIres?: string | null;
  task?: string | null;
  proposedEndAtMs?: number;
  entryStartAtMs?: number | null;
}

/** Request payload for the applyOpenTimeTrackingAutoClose Cloud callable. */
export interface ApplyOpenTimeTrackingAutoCloseRequest {
  /** Lock token from the preceding preview call (recommended for safety). */
  lockToken?: string;
  /** Month document ID of the stale entry (YYYY-M{month0}). */
  staleMonthDocId: string;
  /** Day map key of the stale entry (MM-DD). */
  staleDayKey: string;
  /** Entry type. */
  entryType: "project" | "other" | "shared";
  /** Entry ID within the day's map. */
  entryId: string;
  /** Auto-proposed end time in ms; always written to workEndTime/workEndDate. Omit for a review resolve. */
  proposedEndAtMs?: number;
  /** Source used to derive the proposed end time. Omit for a review resolve. */
  proposalSource?: ProposalSource;
  /** User-edited end time in ms; present only when the user changed the proposal in the dialog. */
  userEditedEndAtMs?: number;
  /** Identifies the calling app; written to endSource on closed entries. */
  operationSource: ProjectOperationSource;
  /**
   * Resolve a pending auto-close review instead of closing anything: the entry was already
   * closed by the overnight sweep. Writes `userSetEndOfWork` + `endSource` (only when
   * `userEditedEndAtMs` is given) and clears the `pendingUserReview` flag; closes nothing else.
   * When true, `proposedEndAtMs` / `proposalSource` are not required.
   */
  resolveReview?: boolean;
}

/** Details of the user time entry closed by the apply callable. */
export interface StaleCloseAppliedEntry {
  entryType: "project" | "other" | "shared";
  entryId: string;
  endAtMs: number;
}

/** Details of the project-level log closed alongside the user entry. */
export interface StaleCloseAppliedProjectLog {
  projectId: string;
  projectLogId: string;
  endAtMs: number;
}

/** Project step that was suspended as part of the close operation. */
export interface StaleCloseAppliedProjectStep {
  projectId: string;
  stepIndex: number;
  contactPersonNumber?: number | null;
}

/** Response from the applyOpenTimeTrackingAutoClose Cloud callable. */
export interface ApplyOpenTimeTrackingAutoCloseResponse {
  wasSuccessful: boolean;
  outcome: StaleCloseOutcome;
  closedEntry?: StaleCloseAppliedEntry;
  closedProjectLog?: StaleCloseAppliedProjectLog | null;
  suspendedProjectStep?: StaleCloseAppliedProjectStep | null;
}
